import { Suspense, useState, useRef, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, MeshReflectorMaterial, BakeShadows, ContactShadows, Environment, Stars} from '@react-three/drei'
import { EffectComposer, Bloom, ToneMapping, ChromaticAberration, Vignette, SMAA, Scanline, Noise, DepthOfField } from '@react-three/postprocessing'
import Scene, { DEFAULT_DEBUG_CONFIG } from './Scene'
import { useGameController, GameUI, Hotspots } from './GameController'
import * as THREE from 'three'
import { useCallback } from 'react'

// ============================================
// UNIFIED SIDE MENU - All controls in one responsive panel
// ============================================
function UnifiedSideMenu({ 
  showDebugPanel, 
  setShowDebugPanel, 
  config, 
  setConfig,
  gameState,
  resetGame,
  skipTurn,
  onPOVClick,
  currentView
}) {
  // Detect if mobile on mount - initialize menu state based on screen size
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [isMenuOpen, setIsMenuOpen] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  );
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const updatePosition = (axis, value) => {
    setConfig(prev => ({
      ...prev,
      cagePosition: { ...prev.cagePosition, [axis]: parseFloat(value) }
    }));
  };
  
  const updateSize = (axis, value) => {
    setConfig(prev => ({
      ...prev,
      cageSize: { ...prev.cageSize, [axis]: parseFloat(value) }
    }));
  };

  const currentTeamName = gameState.currentTeam === 1 ? 'TOKYO' : 'LA';
  const currentTeamColor = gameState.currentTeam === 1 ? '#FFFFC5' : '#4ecdc4';

  return (
    <>
      {/* Menu Toggle Button - Always visible */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="menu-toggle-btn"
        style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: 10002,
          width: '50px',
          height: '50px',
          background: isMenuOpen ? 'rgba(68, 136, 255, 0.9)' : 'rgba(10, 10, 20, 0.9)',
          border: '2px solid #4488ff',
          borderRadius: '12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          transition: 'all 0.3s ease',
          boxShadow: '0 0 20px rgba(68, 136, 255, 0.4)'
        }}
      >
        <span style={{
          width: '22px',
          height: '2px',
          background: isMenuOpen ? '#000' : '#4488ff',
          borderRadius: '2px',
          transition: 'all 0.3s ease',
          transform: isMenuOpen ? 'rotate(45deg) translateY(7px)' : 'none'
        }} />
        <span style={{
          width: '22px',
          height: '2px',
          background: isMenuOpen ? '#000' : '#4488ff',
          borderRadius: '2px',
          transition: 'all 0.3s ease',
          opacity: isMenuOpen ? 0 : 1
        }} />
        <span style={{
          width: '22px',
          height: '2px',
          background: isMenuOpen ? '#000' : '#4488ff',
          borderRadius: '2px',
          transition: 'all 0.3s ease',
          transform: isMenuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none'
        }} />
      </button>

      {/* Side Menu Panel */}
      <div 
        className={`side-menu-panel ${isMenuOpen ? 'menu-open' : 'menu-closed'}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: isMenuOpen ? '320px' : '0px',
          background: 'rgba(10, 10, 20, 0.95)',
          borderRight: isMenuOpen ? '2px solid rgba(68, 136, 255, 0.4)' : 'none',
          zIndex: 10001,
          overflow: 'hidden',
          transition: 'width 0.3s ease',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div style={{
          padding: '80px 20px 20px 20px',
          width: '320px',
          height: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}>
          {/* Title */}
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{
              fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
              fontSize: '22px',
              fontWeight: 900,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: '#4488ff',
              textShadow: '0 0 12px rgba(68, 136, 255, 0.7)',
              margin: 0
            }}>
              HALF LEAGUE
            </h1>
            <p style={{
              fontSize: '9px',
              color: '#666',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginTop: '4px',
              lineHeight: '1.4'
            }}>
              TIME IS A SIMULATION WE ALL AGREE TO
            </p>
          </div>

          {/* Book Buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <a 
              href="https://www.audible.com/pd/Half-League-Audiobook/B0GGLVJ4QN" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                flex: 1,
                padding: '10px 12px',
                fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#000',
                background: '#4488ff',
                border: '2px solid #4488ff',
                borderRadius: '8px',
                textDecoration: 'none',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 15px rgba(68, 136, 255, 0.5)'
              }}
            >
              AUDIBLE
            </a>
            <a 
              href="https://www.amazon.com/dp/B0FV5K87DS" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                flex: 1,
                padding: '10px 12px',
                fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#000',
                background: '#4488ff',
                border: '2px solid #4488ff',
                borderRadius: '8px',
                textDecoration: 'none',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 15px rgba(68, 136, 255, 0.5)'
              }}
            >
              KINDLE
            </a>
          </div>

          {/* Divider */}
          <div style={{ 
            height: '1px', 
            background: 'linear-gradient(90deg, transparent, rgba(68, 136, 255, 0.4), transparent)',
            marginBottom: '20px'
          }} />

          {/* Game Status Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{
              fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
              fontSize: '12px',
              letterSpacing: '2px',
              color: '#4488ff',
              marginBottom: '12px',
              textShadow: '0 0 10px rgba(68, 136, 255, 0.5)'
            }}>
              GAME STATUS
            </h3>
            
            {/* Current Turn */}
            {!gameState.gameOver && (
              <div style={{
                background: `linear-gradient(135deg, ${currentTeamColor}22, ${currentTeamColor}11)`,
                border: `2px solid ${currentTeamColor}`,
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '9px', letterSpacing: '2px', color: '#888', marginBottom: '4px' }}>
                  CURRENT TURN
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold',
                  color: currentTeamColor,
                  fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                  letterSpacing: '3px',
                  textShadow: `0 0 10px ${currentTeamColor}66`
                }}>
                  {currentTeamName}
                </div>
              </div>
            )}

            {/* Moves Info */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '8px',
              fontSize: '10px',
              color: '#aaa',
              letterSpacing: '1px'
            }}>
              <div style={{ 
                background: 'rgba(255, 252, 197, 0.1)', 
                padding: '8px', 
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#FFFFC5', fontWeight: 'bold' }}>TOKYO</div>
                <div>{gameState.team1Moves}/12</div>
              </div>
              <div style={{ 
                background: 'rgba(78, 205, 196, 0.1)', 
                padding: '8px', 
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#4ecdc4', fontWeight: 'bold' }}>LA</div>
                <div>{gameState.team2Moves}/12</div>
              </div>
            </div>

            {/* Coverage */}
            <div style={{ 
              marginTop: '12px',
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '8px',
              fontSize: '10px',
              color: '#888'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div>TOKYO Coverage</div>
                <div style={{ color: '#FFFFC5', fontWeight: 'bold' }}>{gameState.team1Score}%</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div>LA Coverage</div>
                <div style={{ color: '#4ecdc4', fontWeight: 'bold' }}>{gameState.team2Score}%</div>
              </div>
            </div>
          </div>

          {/* Game Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
            <button
              onClick={resetGame}
              style={{
                padding: '10px',
                fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#4488ff',
                background: 'transparent',
                border: '2px solid #4488ff',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              ↺ RESET GAME
            </button>
            <button
              onClick={() => onPOVClick('reset')}
              style={{
                padding: '10px',
                fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#4488ff',
                background: 'transparent',
                border: '2px solid #4488ff',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              ⌂ RESET VIEW
            </button>
            <button
              onClick={skipTurn}
              disabled={gameState.gameOver}
              style={{
                padding: '10px',
                fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: gameState.gameOver ? '#666' : '#ff9f43',
                background: 'transparent',
                border: `2px solid ${gameState.gameOver ? '#444' : '#ff9f43'}`,
                borderRadius: '8px',
                cursor: gameState.gameOver ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: gameState.gameOver ? 0.5 : 1
              }}
            >
              ⏭ SKIP TURN
            </button>
          </div>

          {/* Divider */}
          <div style={{ 
            height: '1px', 
            background: 'linear-gradient(90deg, transparent, rgba(255, 107, 53, 0.4), transparent)',
            marginBottom: '15px'
          }} />

          {/* Debug Toggle */}
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            style={{
              width: '100%',
              padding: '8px',
              fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: showDebugPanel ? '#000' : '#ff6b35',
              background: showDebugPanel ? '#ff6b35' : 'transparent',
              border: '2px solid #ff6b35',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              marginBottom: '10px'
            }}
          >
            {showDebugPanel ? '✕ CLOSE DEBUG' : '⚙ DEBUG OPTIONS'}
          </button>

          {/* Debug Panel */}
          {showDebugPanel && (
            <div style={{
              background: 'rgba(255, 107, 53, 0.05)',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              color: '#fff',
              fontFamily: 'monospace',
              fontSize: '11px'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#4ac4ff', fontSize: '10px' }}>Cage Position</h4>
                {['x', 'y', 'z'].map((axis) => (
                  <div key={axis} style={{ display: 'flex', gap: '6px', marginBottom: '4px', alignItems: 'center' }}>
                    <label style={{ width: '16px', textTransform: 'uppercase' }}>{axis}:</label>
                    <input 
                      type="range" 
                      min={axis === 'y' ? '0' : '-50'} 
                      max={axis === 'y' ? '100' : '50'} 
                      step="1" 
                      value={config.cagePosition[axis]}
                      onChange={(e) => updatePosition(axis, e.target.value)} 
                      style={{ flex: 1 }} 
                    />
                    <span style={{ width: '30px', fontSize: '10px' }}>{config.cagePosition[axis]}</span>
                  </div>
                ))}
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#4ac4ff', fontSize: '10px' }}>Cage Size</h4>
                {[['x', 'W'], ['y', 'H'], ['z', 'D']].map(([axis, label]) => (
                  <div key={axis} style={{ display: 'flex', gap: '6px', marginBottom: '4px', alignItems: 'center' }}>
                    <label style={{ width: '16px' }}>{label}:</label>
                    <input 
                      type="range" 
                      min="5" 
                      max={axis === 'y' ? '80' : '50'} 
                      step="1" 
                      value={config.cageSize[axis]}
                      onChange={(e) => updateSize(axis, e.target.value)} 
                      style={{ flex: 1 }} 
                    />
                    <span style={{ width: '30px', fontSize: '10px' }}>{config.cageSize[axis]}</span>
                  </div>
                ))}
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#4ac4ff', fontSize: '10px' }}>Shape Scale</h4>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input 
                    type="range" 
                    min="0.01" 
                    max="0.5" 
                    step="0.01" 
                    value={config.shapeScale}
                    onChange={(e) => setConfig(prev => ({ ...prev, shapeScale: parseFloat(e.target.value) }))} 
                    style={{ flex: 1 }} 
                  />
                  <span style={{ width: '40px', fontSize: '10px' }}>{config.shapeScale.toFixed(2)}</span>
                </div>
              </div>
              
              <div>
                <h4 style={{ margin: '0 0 8px 0', color: '#4ac4ff', fontSize: '10px' }}>Colors</h4>
                {[
                  ['cageColor', 'Cage Wire'],
                  ['teamAColor', 'Team A'],
                  ['teamBColor', 'Team B'],
                  ['crossSectionColor', 'Cross Section']
                ].map(([key, label]) => (
                  <div key={key} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ width: '80px', fontSize: '10px' }}>{label}:</label>
                    <input 
                      type="color" 
                      value={config[key]}
                      onChange={(e) => setConfig(prev => ({ ...prev, [key]: e.target.value }))} 
                      style={{ width: '30px', height: '20px' }}
                    />
                    <span style={{ fontSize: '9px', color: '#888' }}>{config[key]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Game Over Overlay */}
          {gameState.gameOver && (
            <div style={{
              marginTop: '20px',
              background: 'linear-gradient(135deg, rgba(68, 136, 255, 0.2), rgba(68, 136, 255, 0.1))',
              border: '2px solid #4488ff',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <h2 style={{
                fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                fontSize: '18px',
                color: '#4488ff',
                marginBottom: '10px',
                textShadow: '0 0 15px rgba(68, 136, 255, 0.8)'
              }}>
                GAME OVER
              </h2>
              <div style={{
                fontSize: '14px',
                color: '#fff',
                fontWeight: 'bold'
              }}>
                {gameState.team1Score > gameState.team2Score ? 'TOKYO Wins!' :
                gameState.team2Score > gameState.team1Score ? 'LA Wins!' :
                'Draw!'}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Set initial perspective camera position and rotation to match orthographic version
function InitialCameraSetup({ controlsRef }) {
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls || !controls.object) return;

    // Immediately set the final top-down position (no close-up first)
    // This avoids the "bounce" caused by a staged position animation.
    controls.object.position.set(0, 900, 40); // high Y = top view, slight Z for perspective
    controls.target.set(0, 0, 0);

    // Polar: small angle -> near top-down (but not exactly 0 to preserve perspective)
    const polar = Math.PI / 22; // ~10°
    // Azimuthal: rotate left a bit
    const azimuthal = -Math.PI / 6;// ~ -15° (left). Use positive for right.

    // If your controls implement these helper methods, use them for clean setting:
    try {
      controls.setPolarAngle(polar);
      controls.setAzimuthalAngle(azimuthal);
    } catch (e) {
      // Fallback if those helpers are not present on this controls instance:
      // convert spherical to Cartesian around target and set camera position instead.
      const radius = controls.object.position.distanceTo(controls.target);
      const x = radius * Math.sin(polar) * Math.cos(azimuthal);
      const z = radius * Math.sin(polar) * Math.sin(azimuthal);
      const y = radius * Math.cos(polar);
      controls.object.position.set(x, y, z);
    }

    // Finalize
    controls.update();
  }, [controlsRef]);

  return null;
}

// Optionally configure OrbitControls settings
function CustomOrbitControls({ controlsRef }) {
  useEffect(() => {
    if (controlsRef.current) {
      const controls = controlsRef.current
      controls.enableDamping = true
      controls.dampingFactor = 0.05
      controls.screenSpacePanning = true
      
      // Restrict to near top-down view
      controls.maxPolarAngle = Math.PI / 12;
      controls.minPolarAngle = 0.05; 
      
      controls.minDistance = 1.0
      controls.maxDistance = 250.0
      controls.zoomSpeed = 1.8
      controls.rotateSpeed = 0.7
      controls.enableZoom = true
      controls.panSpeed = 0.5  // Slower pan for better control
      
      // Pan boundaries to keep user from getting lost
      const panBounds = {
        minX: -80,
        maxX: 80,
        minZ: -80,
        maxZ: 80
      }
      
      // Clamp target position within bounds after each change
      const clampTarget = () => {
        if (controls.target) {
          controls.target.x = Math.max(panBounds.minX, Math.min(panBounds.maxX, controls.target.x))
          controls.target.z = Math.max(panBounds.minZ, Math.min(panBounds.maxZ, controls.target.z))
        }
      }
      
      // Listen for control changes to enforce bounds
      controls.addEventListener('change', clampTarget)
      
      // Temporarily disable these constraints when in POV mode
      const updatePOVConstraints = () => {
        if (controlsRef.current) {
          // Store whether we're in a POV
          const isPOV = controlsRef.current._isPOV;
          
          // If in POV, use different constraints
          if (isPOV) {
            // Disable panning and zooming in POV mode
            controls.enablePan = false;
            controls.enableZoom = false;
            controls.enableRotate = false;
            // Allow more freedom to look around
            controls.maxPolarAngle = Math.PI;
            controls.minPolarAngle = 0;
            
            // Optionally adjust rotation speed for more precise control
            controls.rotateSpeed = 0.5;
          } else {
            // Enable panning with controlled speed in normal view
            controls.enablePan = true;
            controls.enableZoom = true;
            controls.enableRotate = true;
            
            // Return to default view constraints
            controls.maxPolarAngle = Math.PI / 3;
            controls.minPolarAngle = 0.05;
            controls.rotateSpeed = 0.7;
            controls.panSpeed = 0.5;  // Keep pan controlled
          }
          
          // Update controls with new settings
          controls.update();
        }
      }
      
      updatePOVConstraints();
      
      // Add event listener for constraint updates
      window.addEventListener('pov-changed', updatePOVConstraints);
      return () => {
        window.removeEventListener('pov-changed', updatePOVConstraints);
        controls.removeEventListener('change', clampTarget);
      };
    }
  }, [controlsRef])
  return null
}

export default function App() {
  const [enableCameraRig, setEnableCameraRig] = useState(false)
  const { gameState, placeShape, resetGame, skipTurn } = useGameController()
  const controlsRef = useRef()

  // DEBUG CONFIG STATE - Set showDebugPanel to true to show the tweaking menu
  const [showDebugPanel, setShowDebugPanel] = useState(false) // <- Debug panel hidden by default
  const [debugConfig, setDebugConfig] = useState(DEFAULT_DEBUG_CONFIG)

  // Add state to track current view for hotspot visibility
  const [currentView, setCurrentView] = useState('reset')
  const [cameraTarget, setCameraTarget] = useState(null)
  const povPositions = {
    newYork: {
      position: [-65, 20, 0],   // Raised higher and not as far back
      lookAt: [0, 55, 0],        // Looking at a slightly lower point
      transition: 2500,          // Increased duration for smoother animation
      isPOV: true
    },
    tokyo: {
      position: [65, 20, 0],    // Symmetrical with NY on other side
      lookAt: [0, 55, 0],         // Same target point
      transition: 2500,          // Increased duration for smoother animation
      isPOV: true
    },
    reset: {
      // Match initial camera animation setup
      position: [0, 900, 40],
      lookAt: [0, 0, 0],
      transition: 2500,  // Longer duration for smoother animation
      isPOV: false
    }
  }
  
  // Handle POV button clicks
  const handlePOVClick = useCallback((povKey) => {
    setCurrentView(povKey) // Update current view for hotspot visibility
    setCameraTarget(povPositions[povKey])
  }, [])

  useEffect(() => {
    if (cameraTarget && controlsRef.current) {
      const controls = controlsRef.current
      const camera = controls.object
      const startPos = camera.position.clone()
      const startTarget = controls.target.clone()
      const endPos = new THREE.Vector3(...cameraTarget.position)
      const endTarget = new THREE.Vector3(...cameraTarget.lookAt)

      controls._isPOV = cameraTarget.isPOV;
      window.dispatchEvent(new Event('pov-changed'));

      const startTime = Date.now()
      const duration = cameraTarget.transition

      const animateCamera = () => {
        const now = Date.now()
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // Improved easing function - smoother cubic easing
        // Use a combined ease-in-out cubic for more natural movement
        let easeProgress
        if (progress < 0.5) {
          // Ease in during first half
          easeProgress = 4 * Math.pow(progress, 3)
        } else {
          // Ease out during second half
          easeProgress = 1 - Math.pow(-2 * progress + 2, 3) / 2
        }

        camera.position.lerpVectors(startPos, endPos, easeProgress)
        controls.target.lerpVectors(startTarget, endTarget, easeProgress)
        controls.update()

        if (progress < 1) requestAnimationFrame(animateCamera)
        else {
          // Ensure we finish exactly at the target position
          camera.position.copy(endPos)
          controls.target.copy(endTarget)
          controls.update()
          
          // Notify about POV change completion
          window.dispatchEvent(new Event('pov-changed'))
        }
      }

      animateCamera()
    }
  }, [cameraTarget])

  return (
    <>
      {/* Unified Side Menu - All controls in one responsive panel */}
      <UnifiedSideMenu 
        showDebugPanel={showDebugPanel} 
        setShowDebugPanel={setShowDebugPanel}
        config={debugConfig}
        setConfig={setDebugConfig}
        gameState={gameState}
        resetGame={resetGame}
        skipTurn={skipTurn}
        onPOVClick={handlePOVClick}
        currentView={currentView}
      />
      <Canvas
        shadows="soft"
        dpr={[1, 1.25]}
        eventSource={document.getElementById('root')}
        eventPrefix="client"
        gl={{ 
          antialias: true,
          alpha: false,
          logarithmicDepthBuffer: false, 
          precision: "mediump",
          powerPreference: "high-performance",
        }}
        onCreated={({ gl }) => {
          gl.sortObjects = false;      
          gl.localClippingEnabled = true;
          gl.clearDepth();
        }}
        camera={{
          // Updated default camera position to be further back
          position: [0, 350, 150],
          fov: 60,
          near: 0.1,
          // Increased far plane to ensure visibility at greater distances
          far: 2000,
        }}
      >
        {/* Background - Pure black for dramatic sci-fi look */}
        <color attach="background" args={['#000000']} />
        <fog attach="fog" args={['#000000', 150, 400]} />

        {/* Lights - OPTIMIZED for performance */}
        <ambientLight intensity={2.8} />
        <hemisphereLight intensity={1.8} groundColor="#1a1a25" color="#252540" />
        <spotLight 
          position={[0, 80, 0]} 
          angle={0.6} 
          penumbra={0.8} 
          intensity={18} 
          distance={200}
          target-position={[0, 0, 0]}
          color="#ffffff"
        />
        {/* Consolidated: 2 main accent lights */}
        <pointLight position={[-50, 35, 0]} intensity={12} distance={180} decay={2} color="#4ac4ff" />
        <pointLight position={[50, 35, 0]} intensity={12} distance={180} decay={2} color="#3fa2ff" />

        {/* Main Scene */}
        <Suspense fallback={null}>
          <Scene gameState={gameState} debugConfig={debugConfig} />
          <Environment preset="night" />
          <Stars radius={300} depth={60} count={400} factor={2} saturation={0} fade speed={0.3} />
          
          {/* Simple black ground plane to hide star background */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -5, 0]}>
            <planeGeometry args={[1000, 1000]} />
            <meshBasicMaterial color="#000000" />
          </mesh>
          
          {/* DISABLED: Reflective ground plane and contact shadows - causing z-fighting
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]}>
            <planeGeometry args={[500, 500]} />
            <MeshReflectorMaterial
              blur={[200, 50]}
              resolution={512}
              mixBlur={0.8}
              mixStrength={400}
              roughness={0.15}
              depthScale={1.5}
              minDepthThreshold={0.2}
              maxDepthThreshold={1.5}
              color="#000000"
              metalness={0.9}
              mirror={0.8}
            />
          </mesh>
          <ContactShadows position={[0, 0, 0]} opacity={0.95} scale={200} blur={2} far={50} color="#000000" />
          */}
          
          {/* Pass currentView to Hotspots to control visibility */}
          <Hotspots 
            onPOVClick={handlePOVClick} 
            currentView={currentView}
            placeShape={placeShape}
            gameState={gameState}
          />
        </Suspense>

        {/* Post-processing effects - DISABLED: Bloom was causing performance issues
        <EffectComposer disableNormalPass multisampling={0}>
          <Bloom 
            luminanceThreshold={0.4} 
            mipmapBlur 
            luminanceSmoothing={0.3} 
            intensity={1.0} 
            radius={0.4}
          />
          <Vignette offset={0.5} darkness={0.5} />
          <ToneMapping />
        </EffectComposer>
        */}

        <OrbitControls
          ref={controlsRef}
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minZoom={5}
          maxZoom={10}
          minPolarAngle={0.05}
          maxPolarAngle={Math.PI / 6} 
          target={[0, 0, 0]}
          enableDamping={true}
          dampingFactor={0.05}
        />

        <CustomOrbitControls controlsRef={controlsRef} />
        <InitialCameraSetup controlsRef={controlsRef} />
        <BakeShadows />
      </Canvas>
    </>
  )
}